import { useCallback, useRef, useState } from 'react';
import { isEmpty } from 'lodash';
import styled from 'styled-components';
import { useFeatureFlagEnabled } from '@frameio/feature-flags';
import type { EncodeStatusEnum } from '@frameio/graphql-client/generated';
import { DEFAULT_TIMECODE } from '@frameio/utils';
import {
  Box,
  Button2,
  IconButton2,
  isMobile,
  Menu,
  MenuGroup,
  MenuRadio,
  PlaceholderCloudArrowUp,
  ScrollContainer,
  Text,
  theme,
  Toggle,
  ToggleCheckbox,
  ToggleLabel,
  Tooltip,
} from '@frameio/vapor';
import { IconChevronDown, IconInfo, IconX } from '@frameio/vapor-icons';
import { FileDropZone } from '@frameio/vapor-migration';
import { useMediaData } from '../../contexts';
import { LanguageMenu } from '../LanguageMenu';
import { StyledButton } from '../LanguageMenu/LanguageMenu';
import TEXT from './generated/TEXT/UploadCaptionsForm';

const DROP_ZONE_HEIGHT_PX = '200px';
const CONTENT_HEIGHT_PX = '190px';
const ENTRY_HEIGHT = 40;
const ENTRY_PADDING = 8;
const ENTRIES_HEIGHT = 3 * ENTRY_HEIGHT + 2 * ENTRY_PADDING;
const allowedExtensions = ['.srt', '.vtt'];
const isCaptionFile = (file: File) =>
  allowedExtensions.some((ext) => file?.name?.toLowerCase()?.endsWith(ext));

TEXT.DEFINE({
  /** Upload button label */
  buttonLabel: 'Upload',
  /** Remove captions button label */
  removeCaptionLabel: 'Remove caption',
  /** Upload captions dialog subtitle */
  subtitle: 'Supported file types include SRT or VTT.',
  /** Choose language dropdown label */
  subtext: 'Choose Language',
  /** Upload captions dialog title */
  title: 'Drag and drop to upload Caption files',
  /**  Mobile upload captions dialog title */
  mobileTitle: 'Upload Caption files',
  /** Tooltip text */
  tooltipText: 'Selected language will appear in Captions menu',
  /** Error message shown when captions uploading failed */
  failedToAdd: 'Failed to add',
  /** Checkbox text for time offset */
  offsetCheckboxLabel: 'Use upload timecode',
  /** Asset start time label text */
  assetStartTimeLabel: 'Asset timecode',
  /** Description informing user that at what timecode their asset starts */
  assetStartTimeDescription: (time: string) =>
    `Caption file assumes playback starts at ${time}`,
  /** Offset option fr assets that start at 0 */
  startAtZeroLabel: 'Upload timecode',
  /** Description for assets that start at 0 time */
  startAtZeroDescription: 'Caption file assumes playback starts at 00:00:00:00',
  /** Caption time menu */
  menuGroupLabel: 'Caption timing',
});

export type CaptionEntry = {
  id: number;
  locale: string;
  file: File;
  encodeStatus: EncodeStatusEnum;
  isValid?: boolean;
  hasOffset?: boolean;
};
export type CaptionEntries = CaptionEntry[] | [];
type UploadCaptionsFormProps = {
  captions: CaptionEntries;
  setCaptions: (
    value: CaptionEntries | ((prev: CaptionEntries | []) => CaptionEntries)
  ) => void;
  handleInvalidFiles?: (invalidFiles: File[], validFiles: File[]) => void;
};
const OffsetOption = ({
  checked,
  startAtZero,
  onSelect,
  assetStartTime,
}: {
  checked: boolean;
  startAtZero: boolean;
  onSelect: () => void;
  assetStartTime?: string | null;
}) => {
  const label = startAtZero ? TEXT.startAtZeroLabel : TEXT.assetStartTimeLabel;
  return (
    <MenuRadio checked={checked} name={label} onSelect={onSelect}>
      <Box paddingY={8}>
        <Text variant="body">{label}</Text>
        <Text color="tertiary" variant="secondary">
          {startAtZero
            ? TEXT.startAtZeroDescription
            : String(TEXT.assetStartTimeDescription(assetStartTime || ''))}
        </Text>
      </Box>
    </MenuRadio>
  );
};
export const UploadCaptionsForm = ({
  captions,
  setCaptions,
  handleInvalidFiles,
}: UploadCaptionsFormProps) => {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const isOffsetFeatureFlagEnabled = useFeatureFlagEnabled(
    'v4.captions_upload_timecode_offset'
  );
  const [hasCustomOffset, setHasCustomOffset] = useState(false);
  const hasFiles = captions.length > 0;
  const openFilerPicker = () => {
    fileInputRef?.current?.click();
  };
  const mediaData = useMediaData();
  const assetStartTime =
    mediaData &&
    'timecode' in mediaData &&
    mediaData.timecode &&
    mediaData.timecode !== DEFAULT_TIMECODE
      ? mediaData.timecode
      : null;
  const showOffsetMenu =
    hasCustomOffset && isOffsetFeatureFlagEnabled && assetStartTime;
  const setStartTimeValue = (index: number, startAtZero: boolean) => {
    setCaptions((prevCaptions: CaptionEntries) => {
      const newCaptions = [...prevCaptions];
      newCaptions[index] = captions[index];
      newCaptions[index].hasOffset = !startAtZero;
      return newCaptions;
    });
  };
  const handleOnChange = (validFiles: File | File[]) => {
    const validFilesArray =
      validFiles instanceof Array ? validFiles : [validFiles];
    if (isEmpty(validFiles)) return;
    const newCaptions = validFilesArray.map((file, i) => {
      return {
        id: i,
        locale: '',
        isValid: true,
        encodeStatus: 'STARTED' as EncodeStatusEnum,
        file,
        hasOffset: false,
      };
    });
    setCaptions((prevCaptions: CaptionEntries) => {
      return [...prevCaptions, ...newCaptions];
    });
  };
  const removeCaptionFile = useCallback(
    (index: number) => {
      setCaptions((prevCaptions: CaptionEntries) => {
        const newCaptions = [...prevCaptions];
        newCaptions.splice(index, 1);
        return newCaptions;
      });
    },
    [setCaptions]
  );
  const selectLanguage = useCallback(
    (index: number, locale: string) => {
      setCaptions((prevCaptions: CaptionEntries) => {
        const newCaptions = [...prevCaptions];
        newCaptions[index] = captions[index];
        newCaptions[index].locale = locale;
        return newCaptions;
      });
    },
    [captions, setCaptions]
  );
  return (
    <Box
      alignItems="center"
      display="flex"
      flexDirection="column"
      justifyContent="center"
      width="100%"
    >
      <FileDropZone
        ref={fileInputRef}
        // Only restricts files in file picker, not drag and drop
        accept=".srt,.vtt"
        allowMultiple={true}
        behavior="display"
        data-testid="upload-captions-input"
        handleInvalidFiles={handleInvalidFiles}
        isDisabled={false}
        onChange={handleOnChange}
        validate={isCaptionFile}
        width={'100%'}
      >
        {({ canDrop }) => {
          return (
            <DropIndicator
              $isDropping={canDrop}
              style={{
                border: `1px dashed ${theme.colors.border.secondary}`,
              }}
            >
              {canDrop && !isMobile && <AnimatedBorder />}
              <Box
                alignItems="center"
                display="flex"
                flexDirection="column"
                gap={8}
                paddingBottom={8}
              >
                <StyledPlaceholder>
                  <PlaceholderCloudArrowUp />
                </StyledPlaceholder>

                <Text variant="link">
                  {isMobile ? TEXT.mobileTitle : TEXT.title}
                </Text>
                <Text color="tertiary" variant="secondary">
                  {TEXT.subtitle}
                </Text>
              </Box>
              <Button2
                data-testid="select-captions"
                onPress={openFilerPicker}
                variant={hasFiles ? 'secondary' : 'primary'}
              >
                {TEXT.buttonLabel}
              </Button2>
            </DropIndicator>
          );
        }}
      </FileDropZone>
      {hasFiles && (
        <ContentContainer>
          {isOffsetFeatureFlagEnabled && assetStartTime && (
            <Box paddingBottom={16} paddingLeft={16}>
              <Tooltip placement="top" title={TEXT.startAtZeroDescription}>
                <Toggle
                  checked={!hasCustomOffset}
                  onChange={() => setHasCustomOffset((prev) => !prev)}
                >
                  <ToggleCheckbox style={{ marginRight: theme.spaces[8] }} />
                  <ToggleLabel
                    color="secondary"
                    enableTooltip={true}
                    variant="body"
                  >
                    {TEXT.offsetCheckboxLabel}
                  </ToggleLabel>
                </Toggle>
              </Tooltip>
            </Box>
          )}
          <Subtitle>
            <Text color="tertiary" variant="body">
              {TEXT.subtext}
            </Text>
            <Tooltip maxWidth="100%" title={TEXT.tooltipText}>
              <IconInfo color={theme.colors.icon.primary} size={16} />
            </Tooltip>
          </Subtitle>

          <ScrollContainer
            enableBottomShadow={captions?.length > 3}
            enableTopShadow={captions?.length > 3}
            paddingX={0}
            paddingY={8}
            shadowSize="medium"
          >
            <Box
              paddingLeft={16}
              paddingRight={16}
              style={{ maxHeight: `${ENTRIES_HEIGHT}px` }}
              width="100%"
            >
              <Box
                display="flex"
                flexDirection="column"
                gap={8}
                paddingBottom={8}
              >
                {captions.map((caption, i) => (
                  <Box
                    key={`${caption.file.name}-${i}`}
                    alignItems="center"
                    backgroundColor={caption.isValid ? 'card' : 'error'}
                    borderColor={caption.isValid ? 'primary' : 'error'}
                    borderRadius={6}
                    display="flex"
                    height={`${ENTRY_HEIGHT}px`}
                    justifyContent="space-between"
                    padding={ENTRY_PADDING}
                    style={{
                      border: `1px solid ${theme.colors.border.primary}`,
                    }}
                  >
                    <Text
                      style={{ flexGrow: 1, flexShrink: 1 }}
                      truncate={{
                        type: 'middle',
                        padding: 0,
                        hasExtension: true,
                      }}
                      variant="body"
                    >
                      {caption.file.name}
                    </Text>
                    <Box
                      alignItems="center"
                      display="flex"
                      gap={4}
                      paddingLeft={8}
                      style={{ flexShrink: 0 }}
                    >
                      {caption.isValid ? (
                        <>
                          <LanguageMenu
                            isAddLanguageMode={true}
                            menuPlacement={
                              showOffsetMenu ? 'bottom-start' : 'bottom'
                            }
                            mode="upload"
                            selectedLanguage={captions[i].locale}
                            selectLocale={(locale) => selectLanguage(i, locale)}
                            style={{
                              height: theme.spaces[24],
                              alignSelf: 'center',
                            }}
                            transcriptions={[]}
                          />
                          {hasCustomOffset &&
                            isOffsetFeatureFlagEnabled &&
                            assetStartTime && (
                              <Menu
                                content={
                                  <MenuGroup label={TEXT.menuGroupLabel}>
                                    <OffsetOption
                                      checked={!captions[i].hasOffset}
                                      onSelect={() =>
                                        setStartTimeValue(i, true)
                                      }
                                      startAtZero={true}
                                    />
                                    <OffsetOption
                                      assetStartTime={assetStartTime}
                                      checked={Boolean(captions[i].hasOffset)}
                                      onSelect={() =>
                                        setStartTimeValue(i, false)
                                      }
                                      startAtZero={false}
                                    />
                                  </MenuGroup>
                                }
                                placement="bottom-start"
                              >
                                <StyledButton
                                  $mode="upload"
                                  $style={{
                                    height: theme.spaces[24],
                                    alignSelf: 'center',
                                  }}
                                  iconAfter={
                                    <IconChevronDown
                                      color={theme.colors.icon.secondary}
                                      size={16}
                                    />
                                  }
                                  spaceBetween={true}
                                >
                                  {captions[i].hasOffset
                                    ? TEXT.assetStartTimeLabel
                                    : TEXT.startAtZeroLabel}
                                </StyledButton>
                              </Menu>
                            )}
                        </>
                      ) : (
                        <Text color="tertiary" variant="secondary">
                          {TEXT.failedToAdd}
                        </Text>
                      )}

                      <IconButton2
                        icon={<IconX color={theme.colors.icon.secondary} />}
                        label={TEXT.removeCaptionLabel}
                        onPress={() => removeCaptionFile(i)}
                        variant="tertiary-dimmed"
                      />
                    </Box>
                  </Box>
                ))}
              </Box>
            </Box>
          </ScrollContainer>
        </ContentContainer>
      )}
    </Box>
  );
};
const DropIndicator = styled.div<{
  $isDropping?: boolean;
  $isMobile?: boolean;
}>`
  align-items: center;
  background-color: ${theme.colors.background.panel};
  border-radius: ${theme.radii[6]};
  display: flex;
  flex-direction: column;
  gap: ${theme.spaces[8]};
  height: ${DROP_ZONE_HEIGHT_PX};
  justify-content: center;
  margin: ${theme.spaces[16]};
  position: relative;
  transition: border-color ${theme.transitions.durations[300]}
    ${theme.transitions.eases.primary};

  ${({ $isDropping }) =>
    $isDropping && `border-color: ${theme.colors.brand.interactive.hover};`};
`;
const Subtitle = styled(Box)`
  align-items: center;
  background-color: ${theme.colors.components.modal.background};
  display: flex;
  gap: ${theme.spaces[4]};
  justify-content: flex-start;
  padding-left: ${theme.spaces[16]};
  padding-right: ${theme.spaces[16]};
`;
const ContentContainer = styled(Box)`
  max-height: ${CONTENT_HEIGHT_PX};
  width: 100%;
`;
const AnimatedBorder = () => {
  const strokeColor = theme.colors.border.interactive.selected;
  return (
    <svg
      height="100%"
      preserveAspectRatio="none"
      style={{
        position: 'absolute',
        margin: 'auto',
        right: 0,
        left: 0,
        top: 0,
        bottom: 0,
        pointerEvents: 'none',
      }}
      width="100%"
      xmlns="http://www.w3.org/2000/svg"
    >
      <style>
        {`
          @keyframes dash-move {
            0% {
              stroke-dashoffset: 0;
            }
            100% {
              stroke-dashoffset: -20;
            }
          }
        `}
      </style>
      <rect
        fill="none"
        height="calc(100% - 1px)"
        rx="6"
        stroke={strokeColor}
        strokeDasharray="8 2"
        strokeDashoffset="0"
        style={{
          animation: 'dash-move 1s linear infinite',
        }}
        width="calc(100% - 1px)"
        x="0.5"
        y="0.5"
      />
    </svg>
  );
};
const StyledPlaceholder = styled(Box)`
  height: 64px;
  width: 80px;

  svg {
    height: 100%;
    width: 100%;
  }
`;
